import { buildRulePayload } from "../TaskRecurrenceFields";

export const dateInputValue = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

export const timeInputValue = (value) => {
  if (!value) return "09:00";
  const match = String(value).match(/^(\d{2}:\d{2})/);
  return match ? match[1] : "09:00";
};

export const recurrenceSeedFromScheduledDate = (scheduledDate) => {
  const iso = String(scheduledDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return {};
  const [year, month, day] = iso.split("-").map(Number);
  return {
    start_date: iso,
    weekdays: [new Date(year, month - 1, day).getDay()],
    month_day: day,
    year_month: month,
  };
};

export const recurrenceValueFromRule = (rule) => {
  const startDate = dateInputValue(rule?.start_date);
  const fallbackMonth = new Date().getMonth() + 1;
  return {
    rule_id: rule?.id || null,
    _syncKey: `rule-${rule?.id || "unknown"}-${rule?.current_version_id || rule?.updated_at || "current"}`,
    enabled: true,
    mode: rule?.mode || "calendar",
    frequency: rule?.frequency || "daily",
    weekdays: Array.isArray(rule?.weekdays)
      ? rule.weekdays.map((weekday) => Number(weekday))
      : [1],
    month_day: rule?.month_day || 1,
    year_month: startDate ? Number(startDate.slice(5, 7)) : fallbackMonth,
    start_date: startDate,
    end_date: dateInputValue(rule?.end_date),
    time_of_day: timeInputValue(rule?.time_of_day),
    timezone: rule?.timezone,
    anchor_type: rule?.anchor_type || "checkout_plus",
    anchor_offset_days: rule?.anchor_offset_days || 0,
    lookahead_count: rule?.lookahead_count || 30,
    rental_scope: rule?.rental_scope,
    rental_ids: Array.isArray(rule?.rental_ids)
      ? rule.rental_ids.map((rentalId) => Number(rentalId))
      : [],
    default_assignees: Array.isArray(rule?.default_assignees)
      ? rule.default_assignees
      : [],
    checklist_template_id: rule?.checklist_template_id || null,
    reminders: Array.isArray(rule?.reminders) ? rule.reminders : undefined,
  };
};

export const scheduledForDisplayValue = ({ existingSchedule = null, workOrder = null } = {}) => {
  const scheduledDate = dateInputValue(
    existingSchedule?.scheduled_date || workOrder?.scheduled_date || null,
  );
  if (!scheduledDate) return null;
  const rawStartTime =
    existingSchedule?.scheduled_start_time || workOrder?.scheduled_start_time || null;

  return {
    scheduledDate,
    scheduledStartTime: rawStartTime ? timeInputValue(rawStartTime) : null,
  };
};

export const buildOccurrenceSchedulePayload = ({
  scheduledDate,
  scheduledStartTime,
  durationMinutes,
  scheduledBy,
  assigners = [],
}) => {
  const date = dateInputValue(scheduledDate);
  const startTime = scheduledStartTime ? timeInputValue(scheduledStartTime) : "";
  if (!date || !startTime) return null;

  const duration = Number(durationMinutes) || 60;
  const [startHour, startMinute] = startTime.split(":").map(Number);
  const end = new Date();
  end.setHours(startHour, startMinute, 0, 0);
  end.setMinutes(end.getMinutes() + duration);
  const endTime = `${String(end.getHours()).padStart(2, "0")}:${String(
    end.getMinutes()
  ).padStart(2, "0")}`;

  return {
    scheduled_date: date,
    scheduled_start_time: startTime,
    scheduled_end_time: endTime,
    scheduled_by: scheduledBy ?? null,
    assigners,
  };
};

export const pickFutureRuleScheduleTime = ({
  recurrenceTime,
  scheduleTime,
  existingScheduleStartTime,
  preferredSource = "auto",
}) => {
  const nextRecurrenceTime = timeInputValue(recurrenceTime);
  const nextScheduleTime = scheduleTime ? timeInputValue(scheduleTime) : "";
  const baselineTime = existingScheduleStartTime
    ? timeInputValue(existingScheduleStartTime)
    : "";

  if (preferredSource === "recurrence") {
    return nextRecurrenceTime || nextScheduleTime || baselineTime || "09:00";
  }
  if (preferredSource === "schedule") {
    return nextScheduleTime || nextRecurrenceTime || baselineTime || "09:00";
  }

  const recurrenceChanged =
    Boolean(nextRecurrenceTime) &&
    Boolean(baselineTime) &&
    nextRecurrenceTime !== baselineTime;
  const scheduleChanged =
    Boolean(nextScheduleTime) &&
    Boolean(baselineTime) &&
    nextScheduleTime !== baselineTime;

  if (recurrenceChanged && !scheduleChanged) return nextRecurrenceTime;
  if (scheduleChanged && !recurrenceChanged) return nextScheduleTime;

  return nextRecurrenceTime || nextScheduleTime || baselineTime || "09:00";
};

export const buildFutureRuleUpdatePayload = ({
  recurrence,
  data,
  assignScheduleStartTime,
  existingScheduleStartTime,
  assignScheduleDuration,
  selectedChecklistTemplate,
  checklistTemplateTouched = false,
  rentalIds,
  timePreference = "auto",
  editMode = "future_only",
}) => {
  const hasExplicitAssigners = Object.prototype.hasOwnProperty.call(
    data || {},
    "assigners"
  );
  const sourceAssignees = hasExplicitAssigners
    ? (data.assigners || [])
    : (recurrence?.default_assignees || []);
  const defaultAssignees = sourceAssignees
    .map((a) => ({
      type: a.type || a.assignee_type,
      id: Number(a.id || a.assignee_id),
      assignment_role: a.assignment_role || "assignee",
    }))
    .filter((a) => a.type && a.id);

  const checklistTemplateId = checklistTemplateTouched
    ? (selectedChecklistTemplate || null)
    : (selectedChecklistTemplate ||
      recurrence?.checklist_template_id ||
      null);

  const scheduleTime = pickFutureRuleScheduleTime({
    recurrenceTime: recurrence?.time_of_day,
    scheduleTime: assignScheduleStartTime,
    existingScheduleStartTime,
    preferredSource: timePreference,
  });
  const durationMins =
    Number(assignScheduleDuration) || Number(recurrence?.duration_minutes) || 60;

  return {
    ...buildRulePayload(
      { ...recurrence, time_of_day: scheduleTime, duration_minutes: durationMins },
      {
        name: data.summary,
        summary: data.summary,
        task_type: data.task_type,
        priority: data.priority,
        category: data.category,
        description: data.description,
        rental_ids: rentalIds,
        default_assignees: defaultAssignees,
        checklist_template_id: checklistTemplateId,
      }
    ),
    time_of_day: scheduleTime,
    duration_minutes: durationMins,
    edit_mode: editMode,
  };
};
